/**
 * CAMPAIGN W1.5 — the client campaign director. Thin, stateful glue around the
 * PURE sim-core reducer (stepCampaign): forwards typed events, and on a beat
 * change performs the beat's declared side effects — scripted signals,
 * objectives, chapter cards, comms scripts, Roll entries. No Three.js here.
 */
import {
  stepCampaign,
  initialCampaignState,
  beatById,
  makeScriptedSignal,
  type CampaignBeat,
  type CampaignEvent,
  type CampaignState,
  type SignalAnchor,
  type StorySignalSpec,
  type Objective,
  type SavedCampaign,
  type SignalSource,
  type Vec3,
} from '@blackmarble/sim-core';
import type { SignalManager } from '../sim/signals.js';
import type { CommsPanel } from '../hud/commsPanel.js';
import type { ChapterCard } from '../hud/chapterCard.js';

export interface DirectorDeps {
  signalMgr: SignalManager;
  /** Resolve an anchor to a world position (manifest body lookup lives in main). */
  resolveAnchor: (anchor: SignalAnchor) => Vec3 | null;
  /** Push (or replace by id) an objective and make it active. */
  pushObjective: (o: Objective) => void;
  /** Lock a beat's marker signal as the nav-target (heading bracket + arrow), so a
   * beat like the interdiction reads as a place to fly to. Null is a no-op nudge. */
  markObjectiveSignal: (sig: SignalSource | null) => void;
  /** Name someone on the Roll + play the death tone (sombre side effect). */
  rollEntry: (name: string) => void;
  commsPanel: CommsPanel;
  chapterCard: ChapterCard;
  /** Suppress / release the CLICK TO FLY overlay around the cold open. */
  suppressStartup: () => void;
  releaseStartup: () => void;
}

const COLD_OPEN_BEAT = 'a0-cold-open';

export class CampaignDirector {
  private state: CampaignState;
  private spawnedSignalIds: string[] = [];
  /** §8 — set by carveCenotaph ([V] at the cold-open wall); read by departCenotaph
   * to pick the reading the undock records. Director-local UI state, never persisted
   * (the persisted flag is `cenotaphChoice`, written by the reducer on departure). */
  private coldOpenCarved = false;

  constructor(
    private readonly beats: readonly CampaignBeat[],
    saved: SavedCampaign | null,
    hasLegacySave: boolean,
    private readonly deps: DirectorDeps,
  ) {
    if (saved) {
      this.state = {
        act: saved.act as CampaignState['act'],
        beatId: saved.beatId,
        flags: saved.flags,
      };
    } else if (hasLegacySave) {
      // Pre-campaign save: never yank an existing pilot back into the cold open.
      const postCold = beats.find((b) => b.id !== COLD_OPEN_BEAT);
      this.state = {
        act: postCold?.act ?? 0,
        beatId: postCold?.id ?? COLD_OPEN_BEAT,
        flags: { coldOpenSeen: true, cenotaphChoice: 'finished' },
      };
    } else {
      this.state = initialCampaignState(beats);
    }
    this.deps.signalMgr.suppressLegacyStory = true;
    this.enterBeat(this.state.beatId, true);
  }

  /** The campaign slice for snapshot() (save v4). */
  get saveSlice(): SavedCampaign {
    return { act: this.state.act, beatId: this.state.beatId, flags: { ...this.state.flags } };
  }

  /** Debug / HUD introspection. */
  get current(): CampaignState {
    return this.state;
  }

  /**
   * Death-resume: rewind the director's slice (act/beat/flags) to a saved snapshot
   * IN PLACE. The full-reload boot path already reconstructs this exact state via
   * the constructor's `saved` branch; the in-place restore path must do the same or
   * the campaign skews when a pilot dies after a beat advance. Re-enters the beat as
   * a resume (replays comms that gate advancement, skips the chapter card). Null
   * (legacy/no campaign slice) leaves the current beat untouched.
   */
  restore(saved: SavedCampaign | null): void {
    if (!saved) return;
    this.state = {
      act: saved.act as CampaignState['act'],
      beatId: saved.beatId,
      flags: { ...saved.flags },
    };
    this.enterBeat(this.state.beatId, true);
  }

  /** True while the pre-flight cold open should hold the sim (cards also hold). The
   * hold now spans the whole cold-open beat — through the comms AND the silent
   * Cenotaph Choice that follows it (§8) — releasing only when the choice advances
   * the campaign off a0-cold-open. The player sits at the wall with no timer. */
  get holdSim(): boolean {
    return this.state.beatId === COLD_OPEN_BEAT;
  }

  /** §8 — true once the cold-open comms has closed and the silent Cenotaph Choice
   * is awaiting the player's hand. No menu, no prompt: the caller reads raw input
   * and calls carveCenotaph ([V]) and/or departCenotaph (undock [G]). Stays true
   * after a carve — carving does not leave the wall; only the undock does. */
  get coldOpenChoicePending(): boolean {
    return this.state.beatId === COLD_OPEN_BEAT && !this.deps.commsPanel.isOpen;
  }

  /** §8 — [V] finishes the K at the wall. Per the beat table the choice is split
   * from the exit: carving only records WHICH reading the departure will write; it
   * does NOT advance the beat (the undock affordance simply becomes available, and
   * the player may sit indefinitely). Idempotent; returns true on the first carve
   * so the caller may fire one-time visual feedback. Silent by contract — no chime. */
  carveCenotaph(): boolean {
    if (this.coldOpenCarved) return false;
    this.coldOpenCarved = true;
    return true;
  }

  /** §8 — leaving the wall (undock [G]) is the single action that both records the
   * reading and advances to first flight: 'finished' if the K was carved, else
   * 'left' (half-cut). One press — at the Cenotaph the ship is IDLE (the monument
   * is a signal, not a station), so the departure IS the beat advance; there is no
   * docking-FSM round-trip and no second press. Same flag key/values as the retired
   * [1]/[2] menu, so saves stay compatible and the Waypoint-7 somatic callback
   * (statistical, never flag-gated) is untouched. */
  departCenotaph(): void {
    this.onEvent({
      type: 'CENOTAPH_CHOICE_MADE',
      value: this.coldOpenCarved ? 'finished' : 'left',
    });
  }

  /** Forward a typed event into the reducer; act on a beat change. */
  onEvent(ev: CampaignEvent): void {
    const next = stepCampaign(this.state, ev, this.beats);
    if (next === this.state) return;
    const moved = next.beatId !== this.state.beatId;
    this.state = next;
    if (moved) this.enterBeat(next.beatId, false);
  }

  /** Dev fast-travel (debug hook): jump straight to a beat. */
  gotoBeat(id: string): string {
    const beat = beatById(this.beats, id);
    if (!beat) return `no beat ${id}`;
    this.state = { act: beat.act, beatId: beat.id, flags: this.state.flags };
    this.enterBeat(beat.id, false);
    return `at ${beat.id} (act ${beat.act})`;
  }

  /** Perform a beat's declared side effects. `resume` skips the chapter card
   * (presentation) but REPLAYS comms (they gate advancement). */
  private enterBeat(id: string, resume: boolean): void {
    const beat = beatById(this.beats, id);
    if (!beat) return;
    this.swapSignals(beat.storySignals ?? []);
    if (beat.markerSignalId) {
      const marker = this.deps.signalMgr.signals.find((sig) => sig.id === beat.markerSignalId);
      if (marker) this.deps.markObjectiveSignal(marker);
    }
    if (beat.objective) this.deps.pushObjective({ ...beat.objective });
    if (beat.rollEntry && !resume) this.deps.rollEntry(beat.rollEntry);
    if (beat.chapterCard && !resume) {
      this.deps.chapterCard.show(beat.chapterCard.title, beat.chapterCard.sub);
    }
    if (beat.id === COLD_OPEN_BEAT) this.deps.suppressStartup();
    else this.deps.releaseStartup();
    if (beat.comms) {
      this.deps.commsPanel.play(
        beat.comms,
        (commsId) => this.onEvent({ type: 'COMMS_DONE', commsId }),
        (commsId, key) => this.onEvent({ type: 'CHOICE', commsId, key }),
      );
    }
  }

  /** Replace the previous beat's scripted signals with the new set. */
  private swapSignals(specs: readonly StorySignalSpec[]): void {
    const keep = new Set(specs.map((sp) => sp.id));
    for (const old of this.spawnedSignalIds) {
      if (!keep.has(old)) this.deps.signalMgr.removeScripted(old);
    }
    const spawned: string[] = [];
    for (const sp of specs) {
      const base = this.deps.resolveAnchor(sp.anchor);
      if (!base) continue;
      const pos = { x: base.x + sp.offsetKm.x, y: base.y + sp.offsetKm.y, z: base.z + sp.offsetKm.z };
      this.deps.signalMgr.addScripted(
        makeScriptedSignal(
          {
            id: sp.id,
            kind: sp.kind,
            title: sp.title,
            threat: sp.threat,
            seed: sp.seed,
            ...(sp.markerOffsetKm ? { markerOffsetKm: sp.markerOffsetKm } : {}),
            ...(sp.record ? { record: sp.record } : {}),
            ...(sp.faction ? { faction: sp.faction } : {}),
            ...(sp.enemyType ? { enemyType: sp.enemyType } : {}),
          },
          pos,
        ),
      );
      spawned.push(sp.id);
    }
    this.spawnedSignalIds = spawned;
  }
}
